import {
    DollarSign,
    Package,
    ArrowUpRight,
    RefreshCcw,
    Activity,
    Eye,
    Truck, 
    XCircle, 
    CheckCircle, 
    FilePlus2,
    PackageCheck,
    HelpCircle,
    Undo2,
    Ban,
    Copy,
    User,
    Wallet,
    Headset, ScanLine, ArrowDown,
    MapPin
} from "lucide-react"
import { useState } from "react";
import { Link } from "react-router-dom"; 
import { useAuth } from "../../../context/AuthProvider";
import { useLanguage } from "../../../context/LanguageProvider";
import { BASE_URL } from '../../../apiConfig';
import DashboardSkeleton from "../../../cors/DashboardSkeleton";
import { toast } from 'sonner';
import BaarChart from "../../../ui/BaarChart";
import LinneChart from "../../../ui/LinneChart";
import RadialChart from "../../../ui/RadialChart";
import BaarChartConfirmator from "../../../ui/BaarChartConfirmator";
import DeliveryTimeChart from "../../../ui/DeliveryTimeChart";
import SelectDate from "../../../cors/SelectDate";
import Select from "react-select";
import axios from "axios"; 
import Map from "../../../cors/Map";
import ToggleShowStats from "../../../cors/ToggleShowStats";
import { useTheme } from '../../../context/ThemeProvider';

export default function DashboardAdmin() {
  const { token, role, roles } = useAuth();
  const { t } = useLanguage();
  const { isDark } = useTheme();

  const [state, setState] = useState({
    total_revenue: 0,
    total_profit_sellers: 0,
    total_profit_confirmator: 0,
    total_profit: 0,
    total_orders: 0,
    orders_status: {},
    top_products: [],
    top_sellers: [],
    top_cities: []
  });
  const [loading, setLoading] = useState(true);
  const [showContent, setShowContent] = useState(false);
  const [showStatsValues, setShowStatsValues] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [options, setOptions] = useState([]);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [loadingSellers, setLoadingSellers] = useState(false);

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    setLoading(true);
    setShowContent(false);
    const sellerId = selectedSeller && selectedSeller.value !== '' ? selectedSeller.value : null;
    fetchStats(range, sellerId);
  };

  const handleSellerChange = (selectedOption) => {
    setSelectedSeller(selectedOption);
    setLoading(true);
    setShowContent(false);
    
    if (selectedOption && selectedOption.value !== '') {
      fetchStats(dateRange, selectedOption.value);
    } else {
      fetchStats(dateRange, null);
    }
  };
  
  const fetchStats = async (range, sellerId = null) => {
    try {
      const requestBody = { range };
      if (sellerId) {
        requestBody.seller_id = sellerId;
      }

      const response = await fetch(BASE_URL + '/api/dashboard-stats', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API Error: ${response.status} - ${errorText}`);
        throw new Error(`Failed to fetch dashboard stats: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.code === 200 && result.data) {
        setState(result.data);
        setTimeout(() => setShowContent(true), 100);
      } else {
        console.error("Unexpected API response structure:", result);
        toast.error(t('errors.unexpected_format'));
        setShowContent(true); 
      }
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error(err.message || t('errors.loading_stats'));
      setShowContent(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchSellers = async () => {
    try {
      setLoadingSellers(true);
      const res = await axios.get(`${BASE_URL}/api/search-saller`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const sellers = res.data.data;

      const sellerOptions = [
        { value: '', label: t('dashboard.all_sellers') },
        ...sellers.map((s) => ({
          value: s.id,
          label: `${s.name ?? t('dashboard.unknown')} (${s.name_store ?? t('dashboard.no_store')}) - ${s.status}`,
        }))
      ];

      setOptions(sellerOptions);
      setSelectedSeller(sellerOptions[0]);
    } catch (err) {
      console.error("Error fetching sellers:", err);
      toast.error(t('errors.loading_sellers'));
      setOptions([{ value: '', label: t('dashboard.all_sellers') }]);
      setSelectedSeller({ value: '', label: t('dashboard.all_sellers') });
    } finally {
      setLoadingSellers(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "nouveau":
        return "bg-blue-100 text-blue-700 border border-blue-200";
      case "confirme":
        return "bg-green-100 text-green-700 border border-green-200";
      case "suivi":
        return "bg-amber-100 text-amber-700 border border-amber-200";
      case "reporte":
        return "bg-yellow-100 text-yellow-700 border border-yellow-200";
      case "indelivery":
        return "bg-indigo-100 text-indigo-700 border border-indigo-200";
      case "livre":
        return "bg-emerald-100 text-emerald-700 border border-emerald-200";
      case "retour":
        return "bg-orange-100 text-orange-700 border border-orange-200";
      case "refuse":
        return "bg-red-100 text-red-700 border border-red-200";
      case "annule":
        return "bg-gray-200 text-gray-700 border border-gray-300";
      case "duplicated":
        return "bg-pink-100 text-pink-700 border border-pink-200";
      default:
        return "bg-gray-100 text-gray-700 border border-gray-200";
    }
  };

  const getStatusText = (status, t) => {
    switch (status) {
      case "nouveau": return t('status.nouveau');
      case "confirme": return t('status.confirme');
      case "reporte": return t('status.reporte');
      case "suivi": return t('status.suivi');
      case "indelivery": return t('status.indelivery');
      case "livre": return t('status.livre');
      case "retour": return t('status.retour');
      case "refuse": return t('status.refuse');
      case "annule": return t('status.annule');
      case "duplicated": return t('status.duplicated');
      default: return status;
    }
  };

  const getStatusIcon = (status) => {
    const iconSize = 40;
    switch (status) {
      case "nouveau":
        return <FilePlus2 size={iconSize} />;
      case "confirme":
        return <CheckCircle size={iconSize} />;
      case "suivi":
        return <Headset size={iconSize} />;
      case "reporte":
        return <HelpCircle size={iconSize} />;
      case "indelivery":
        return <Truck size={iconSize} />;
      case "livre":
        return <PackageCheck size={iconSize} />;
      case "retour":
        return <Undo2 size={iconSize} />;
      case "refuse":
        return <XCircle size={iconSize} />;
      case "annule":
        return <Ban size={iconSize} />;
      case "duplicated":
        return <Copy size={iconSize} />;
      default:
        return null;
    }
  }

  function formatCount(amount) {
    return new Intl.NumberFormat('en-US', {
    }).format(amount || 0);
  }

  function formatMoney(amount) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  }

  const stats = [
    {
      title: t('stats.revenue'),
      value: `${formatMoney(state?.total_revenue)} MAD`,
      initialValue: "0.00 MAD",
      change: "+20.1%", 
      icon: DollarSign,
      color: "bg-green-500",
    },
    {
      title: t('stats.sellers_profit'),
      value: `${formatMoney(state?.total_profit_sellers)} MAD`,
      initialValue: "0.00 MAD",
      change: "+2.1%",
      icon: User, 
      color: "bg-orange-500",
    },
    {
      title: t('stats.confirmator_profit'),
      value: `${formatMoney(state?.total_profit_confirmator)} MAD`,
      initialValue: "0.00 MAD",
      change: "+2.1%",
      icon: Headset, 
      color: "bg-yellow-500",
    },
    {
      title: t('stats.net_profit'),
      value: `${formatMoney(state?.total_profit)} MAD`,
      initialValue: "0.00 MAD",
      change: "+2.1%",
      icon: Wallet, 
      color: "bg-teal-500",
    },
  ];

  const topProducts = (state.top_products?.length > 0 ? state.top_products : Array(5).fill({
    name: t('dashboard.loading'),
    image_py: "",
    total_quantity: "0",
    total_revenue: "0.00"
  })).map(product => ({
    name: product.name,
    image: product.image_py,
    sales: parseInt(product.total_quantity) || 0,
    revenue: `${formatMoney(product.total_revenue)} MAD`,
    progress: Math.min(85, ((parseInt(product.total_quantity) || 0) / (state?.total_orders || 1)) * 100),
  }));

  const topSallers = (state.top_sellers?.length > 0 ? state.top_sellers : Array(5).fill({
    name: t('dashboard.loading'),
    name_store: "",
    total_orders: "0",
    total_revenue: "0.00"
  })).map(saller => ({
    name: saller.seller_name,
    name_store: saller.store_name,
    total_orders: saller.total_orders,
    revenue: `${formatMoney(saller.total_revenue)} MAD`,
    progress: Math.min(85, ((parseInt(saller.total_orders) || 0) / (state?.total_orders || 1)) * 100),
  }));

  return (
    <div className="min-h-screen">
      <div className="flex">
        <main className="flex-1 md:ml-0">
          <div className="py-8">
            <div className="mx-auto px-4 sm:px-6 px-5">
              <div className={`mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between transition-all duration-200 `}>
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent">
                      {t('dashboard.title')}
                    </h1>
                  </div>
                  <p className="text-sm max-xl:text-xs text-gray-600 max-w-2xl">
                    {t('dashboard.subtitle_admin')}
                  </p>
                </div>
                <div
                 onClick={  fetchSellers }
                 className="flex max-xl:grid items-center gap-3 max-lg:mt-10">
                  <Select
                    instanceId={1}
                    options={options}
                    value={selectedSeller}
                    isLoading={loadingSellers}
                    placeholder={t("dashboard.search_seller")}
                    onChange={handleSellerChange}
                    isClearable={false}
                    className="text-xs"
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        cursor: "pointer",
                        minWidth: "16rem",
                        border: "none",
                        borderRadius: "16px",
                        boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
                        backgroundColor: isDark ? "#292524" : "#ffffff", // stone-800
                        color: isDark ? "#ffffff" : "#000000",
                        "&:hover": {
                          border: "none",
                        },
                      }),

                      menu: (provided) => ({
                        ...provided,
                        backgroundColor: isDark ? "#292524" : "#ffffff",
                        color: isDark ? "#ffffff" : "#000000",
                      }),

                      option: (provided, state) => ({
                        ...provided,
                        cursor: "pointer",
                        backgroundColor: state.isFocused
                          ? isDark
                            ? "#44403c"
                            : "#f3f4f6"
                          : isDark
                          ? "#292524"
                          : "#ffffff",
                        color: isDark ? "#ffffff" : "#000000",
                      }),

                      singleValue: (provided) => ({
                        ...provided,
                        color: isDark ? "#ffffff" : "#000000",
                      }),

                      input: (provided) => ({
                        ...provided,
                        color: isDark ? "#ffffff" : "#000000",
                      }),

                      placeholder: (provided) => ({
                        ...provided,
                        color: isDark ? "#a8a29e" : "#6b7280",
                      }),

                      dropdownIndicator: (provided) => ({
                        ...provided,
                        color: isDark ? "#ffffff" : "#6b7280",
                      }),

                      indicatorSeparator: () => ({
                        display: "none",
                      }),

                      valueContainer: (provided) => ({
                        ...provided,
                        padding: "0 13px",
                      }),
                    }}
                  />
                  <SelectDate onDateRangeChange={handleDateRangeChange} />
                  <button 
                    className="flex items-center gap-1 max-w-max rounded-xl z-50 bg-white dark:bg-stone-800 dark:text-white shadow-sm py-2 px-4 cursor-pointer text-xs transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => {
                      setLoading(true);
                      setShowContent(false);
                      const sellerId = selectedSeller && selectedSeller.value !== '' ? selectedSeller.value : null;
                      fetchStats(dateRange, sellerId);
                    }}
                  >
                    <RefreshCcw 
                      className={`w-3.5 h-3.5 transition-transform duration-500 ease-in-out ${
                        loading ? 'animate-spin' : ''
                      }`} 
                    />
                    <span>{loading ? t('dashboard.refreshing') : t('dashboard.refresh')}</span>
                  </button>
                </div>
              </div>
              <div className="flex items-center mb-2 px-2 w-full justify-end">
                <ToggleShowStats showStatsValues={showStatsValues} setShowStatsValues={setShowStatsValues} />
              </div>
              {loading ? (
                <DashboardSkeleton showConfirmators />
              ) : (
                <>
              {
                role === 1 || (role === 2 && roles.view_statistics === 1) ?
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4 mb-10">
                      {stats.map((stat, index) => (
                        <div
                          key={stat.title}
                          className="group relative bg-white rounded-3xl border border-gray-100 transition-all duration-500 ease-out hover:-translate-y-1"
                        >
                          <div className={`absolute inset-0 rounded-3xl ${stat.color} opacity-0 opacity-10 transition-opacity duration-700 blur-md`} />
                          
                          <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0.5 ${stat.color} rounded-full group-hover:w-1/2 transition-all duration-700 ease-out`} />
                          
                          <div className="relative p-6">
                            <div className="flex items-start gap-4">
                              {/* Minimal icon container */}
                              <div className={`flex-shrink-0 ${stat.color} p-2.5 rounded-2xl transition-all duration-500 shadow-md scale-105`}>
                                <stat.icon className="h-5 w-5 text-white" />
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                {/* Clean title */}
                                <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                                  {stat.title}
                                </p>
                                
                                {/* Bold value with clean typography */}
                                <p className="mt-1.5 text-2xl font-light text-gray-900 tracking-tight">
                                  {showStatsValues ? (showContent ? stat.value : stat.initialValue) : '••••••'}
                                </p>
                                
                                {/* Minimal change indicator */}
                                <div className="flex items-center gap-2 mt-1.5">
                                  <div className={`flex items-center gap-0.5 ${parseFloat(stat.change) > 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                                    <span className="text-xs font-medium">{stat.change}</span>
                                    <span className="text-[10px] font-light">
                                      {parseFloat(stat.change) > 0 ? '↑' : '↓'}
                                    </span>
                                  </div>
                                  <span className="text-[10px] text-gray-300 font-light">vs last month</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Minimal progress bar */}
                            <div className="mt-4 h-[2px] bg-gray-100 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${stat.color} rounded-full transition-all duration-1000 ease-out`}
                                style={{ 
                                  width: `${Math.floor(Math.random() * 40 + 60)}%`,
                                  transitionDelay: `${index * 150}ms`
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>                    
                  :
                  ''
              }
                <div>
                  <div>
                      <div className={`mb-10 grid grid-cols-5 max-lg:gap-2 max-lg:grid-cols-2 gap-5 transition-all duration-500 delay-500 ${
                          showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                        }`}>

                            <Link
                              to="/orders/tout"
                              className={`group relative rounded-2xl overflow-hidden transition-all ${showContent ? "scale-100" : "scale-95"} duration-300 hover:scale-[1.02] hover:shadow-[0_20px_60px_-20px_rgba(16,185,129,0.3)]`}
                            >
                              <div className="absolute inset-0  bg-emerald-300/40  rounded-2xl" />
                              <div className="absolute inset-0 backdrop-blur-2xl bg-white/30 dark:bg dark:bg-stone-800/60 rounded-2xl" />
                              <div className="absolute inset-0 rounded-2xl border border-white/50 dark:border-stone-700 group-hover:border-white/80 transition-colors duration-300" />
                              <div className="absolute -top-20 -right-20 w-48 h-48 bg-emerald-400/20 dark:bg-stone-800/50 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700" />
                              <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-emerald-300/10 dark:bg-stone-800/30 rounded-full blur-3xl" />
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                              
                              <div className="relative p-4 max-md:p-2.5">
                                <div className="flex items-start justify-between">
                                  <div className="space-y-2">
                                    <div className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-white/60 backdrop-blur-sm border border-white/50">
                                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                      <span className="text-[9px] font-semibold text-emerald-600 uppercase tracking-[0.15em]">
                                        {t('dashboard.all')}
                                      </span>
                                    </div>
                                    <p className="text-3xl font-light text-emerald-600 tracking-tight">
                                      {formatCount(state?.total_orders) || 0}
                                    </p>
                                  </div>
                                  
                                  <div className="flex-shrink-0 max-md:p-2 p-2.5 rounded-2xl bg-emerald-100 backdrop-blur-sm border border-white/50 shadow-[0_8px_32px_rgba(16,185,129,0.15)] transition-all duration-500 group-hover:scale-110 group-hover:-rotate-6">
                                    <Package className="h-7 w-7 max-md:w-5 max-md:h-5 text-emerald-500" />
                                  </div>
                                </div>

                                <p className="text-xs text-gray-400/60">Total orders</p>
                                
                                {/* Dynamic Progress */}
                                <div className="mt-2 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[10px] text-gray-400/60">Monthly progress</span>
                                    <span className="text-[10px] font-medium text-emerald-500">
                                      {(() => {
                                        const total = state?.total_orders || 0;
                                        const goal = 1000; 
                                        return `${Math.min(Math.round((total / goal) * 100), 100)}%`;
                                      })()}
                                    </span>
                                  </div>
                                  <div className="flex-1 h-1 rounded-full bg-white/50 backdrop-blur-sm overflow-hidden">
                                    <div 
                                      className="h-full bg-gradient-to-r from-emerald-400 to-emerald-300 rounded-full transition-all duration-1000"
                                      style={{ 
                                        width: `${(() => {
                                          const total = state?.total_orders || 0;
                                          const goal = 500;
                                          return Math.min(Math.round((total / goal) * 100), 100);
                                        })()}%`
                                      }}
                                    >
                                      <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent" />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Link>

                          {/* Status Cards with Dynamic Progress */}
                          {Object.entries(state?.orders_status || {}).map(([statusName, count], index) => {
                            const totalOrders = state?.total_orders || 1; // Avoid division by zero
                            const progressPercentage = Math.round((count / totalOrders) * 100);
                            
                            const statusConfig = {
                              nouveau: { 
                                color: 'bg-blue-300/40 via-blue-300/20 to-blue-50/60', 
                                text: 'text-blue-600 dark:text-blue-400',
                                bg: 'bg-blue-500',
                                light: 'bg-blue-50/60 dark:bg-blue-200/20',
                                icon: FilePlus2,
                              },
                              confirme: { 
                                color: 'bg-emerald-300/40 via-emerald-300/30 to-emerald-50/60', 
                                text: 'text-emerald-600 dark:text-emerald-400',
                                bg: 'bg-emerald-500',
                                light: 'bg-emerald-50/60 dark:bg-emerald-200/20',
                                icon: CheckCircle,
                              },
                              suivi: { 
                                color: 'bg-amber-300/40 via-amber-300/30 to-amber-50/60', 
                                text: 'text-amber-600 dark:text-amber-400',
                                bg: 'bg-amber-500',
                                light: 'bg-amber-50/60 dark:bg-amber-200/20',
                                icon: Headset,
                              },
                              reporte: { 
                                color: 'bg-yellow-300/40 via-yellow-300/30 to-yellow-50/60', 
                                text: 'text-yellow-600 dark:text-yellow-400',
                                bg: 'bg-yellow-500',
                                light: 'bg-yellow-50/60 dark:bg-yellow-200/20',
                                icon: HelpCircle,
                              },
                              indelivery: { 
                                color: 'bg-indigo-300/40 via-indigo-300/30 to-indigo-50/60', 
                                text: 'text-indigo-600 dark:text-indigo-400',
                                bg: 'bg-indigo-500',
                                light: 'bg-indigo-50/60 dark:bg-indigo-200/20',
                                icon: Truck,
                              },
                              livre: { 
                                color: 'bg-emerald-300/40 via-emerald-300/30 to-emerald-50/60', 
                                text: 'text-emerald-600 dark:text-emerald-400',
                                bg: 'bg-emerald-500',
                                light: 'bg-emerald-50/60 dark:bg-emerald-200/20',
                                icon: PackageCheck,
                              },
                              retour: { 
                                color: 'bg-orange-300/40 via-orange-300/30 to-orange-50/60', 
                                text: 'text-orange-600 dark:text-orange-400',
                                bg: 'bg-orange-500',
                                light: 'bg-orange-50/60 dark:bg-orange-200/20',
                                icon: Undo2,
                              },
                              refuse: { 
                                color: 'bg-red-300/40 via-red-300/30 to-red-50/60', 
                                text: 'text-red-600 dark:text-red-400',
                                bg: 'bg-red-500',
                                light: 'bg-red-50/60 dark:bg-red-200/20',
                                icon: XCircle,
                              },
                              annule: { 
                                color: 'bg-gray-300/40 via-gray-300/30 to-gray-50/60', 
                                text: 'text-gray-600 dark:text-gray-400',
                                bg: 'bg-gray-500',
                                light: 'bg-gray-50/60 dark:bg-gray-200/20',
                                icon: Ban,
                              },
                              duplicated: { 
                                color: 'bg-pink-300/40 via-pink-300/30 to-pink-50/60', 
                                text: 'text-pink-600 dark:text-pink-400',
                                bg: 'bg-pink-500',
                                light: 'bg-pink-50/60 dark:bg-pink-200/20',
                                icon: Copy,
                              }
                            };

                            const config = statusConfig[statusName] || statusConfig.nouveau;
                            const IconComponent = config.icon;

                            // Determine progress color based on percentage
                            const getProgressColor = (percentage) => {
                              if (percentage >= 70) return config.bg;
                              if (percentage >= 40) return 'bg-amber-500';
                              return 'bg-red-500';
                            };

                            return (
                              <Link
                                key={index}
                                to={`/orders/${statusName}`}
                                className={`group relative rounded-2xl overflow-hidden transition-all transition-all duration-800 hover:scale-[1.02] ${showContent ? "scale-100" : "scale-95"}`}
                                style={{ animationDelay: `${index * 60}ms` }}
                              >
                                {/* Glass background with status color */}
                                <div className={`absolute inset-0 bg-gradient-to-br ${config.color} rounded-2xl`} />
                                <div className="absolute inset-0 backdrop-blur-xl bg-white/30 dark:bg dark:bg-stone-800/60 rounded-2xl" />
                                <div className="absolute inset-0 rounded-2xl border border-white/50 dark:border-stone-700 group-hover:border-white/70 transition-colors duration-300" />
                                
                                {/* Decorative orb */}
                                <div className={`absolute -top-16 -right-16 w-40 h-40 ${config.bg}/10 dark:bg-stone-800/50 rounded-full blur-3xl group-hover:opacity-70 transition-opacity duration-500`} />
                                <div className={`absolute -bottom-16 -left-16 w-32 h-32 ${config.bg}/5 dark:bg-stone-800/30 rounded-full blur-2xl`} />
                                
                                {/* Animated gradient */}
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                                
                                <div className="relative p-4 max-md:p-2.5">
                                  <div className="flex items-start justify-between">
                                    <div className="space-y-1.5 flex-1 min-w-0">
                                      {/* Status label */}
                                      <div className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full ${config.light} backdrop-blur-sm border border-white/40`}>
                                        <span className={`w-1 h-1 rounded-full ${config.bg} animate-pulse`} />
                                        <span className={`text-[9px] font-medium ${config.text} uppercase tracking-wider`}>
                                          {getStatusText(statusName, t)}
                                        </span>
                                      </div>
                                      
                                      {/* Count with status color */}
                                      <p className={`text-3xl font-light ${config.text} tracking-tight`}>
                                        {formatCount(count)}
                                      </p>
                                      
                                      {/* Percentage of total */}
                                      <p className="text-xs text-gray-400/60 dark:text-white/70">
                                        {progressPercentage}% of total orders
                                      </p>
                                    </div>
                                    
                                    {/* Icon with glass effect */}
                                    <div className={`flex-shrink-0 max-md:p-2 p-2.5 rounded-xl ${config.light} backdrop-blur-sm border border-white/40 transition-all duration-500 group-hover:scale-110 group-hover:-rotate-3 ml-3`}>
                                      <IconComponent className={`h-7 w-7 max-md:w-5 max-md:h-5 ${config.text}`} />
                                    </div>
                                  </div>
                                  
                                  {/* Progress bar with dynamic percentage */}
                                  <div className="mt-2 space-y-1.5">
                                    <div className="flex items-center justify-between">
                                      <span className="text-[10px] text-gray-400/60 dark:text-white/70">Progress</span>
                                      <span className={`text-[10px] font-medium ${config.text}`}>
                                        {progressPercentage}%
                                      </span>
                                    </div>
                                    <div className="h-1 rounded-full bg-white/40 backdrop-blur-sm overflow-hidden">
                                      <div 
                                        className={`h-full ${getProgressColor(progressPercentage)} rounded-full transition-all duration-1000 relative`}
                                        style={{ 
                                          width: `${progressPercentage}%`,
                                          transitionDelay: `${index * 100}ms`
                                        }}
                                      >
                                        <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent" />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      {
                        role === 1 || (role === 2 && roles.view_statistics === 1) ?
                        <div className={` grid max-[1450px]:grid-cols-2 gap-8 grid-cols-3 transition-all duration-700 delay-500 w-full ${
                          showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                        }`}>
                          <div className="lg:col-span-2 space-y-6 max-[1450px]:col-span-2">
                            <div className="w-[100%] grid grid-cols-2   max-sm:grid-cols-1 items-center gap-6">
                              <div className="w-[100%] bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                                <div className="p-4 border-b border-gray-200">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <h3 className="text-sm font-bold text-gray-900">{t('charts.delivered_percentage')}</h3>
                                      <p className="text-[10px] text-gray-600">{t('charts.delivered_percentage_desc')}</p>
                                    </div>
                                  </div>
                                </div>
                                <div className="">
                                  <RadialChart 
                                      percentage={Number(state.delivered_percentage)} 
                                      />  
                                </div>

                              </div>
                              <div className="w-[100%]  bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                                <div className="p-4 border-b border-gray-200">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <h3 className="text-sm font-bold text-gray-900">{t('charts.confirmed_percentage')}</h3>
                                      <p className="text-[10px] text-gray-600">{t('charts.confirmed_percentage_desc')}</p>
                                    </div>
                                  </div>
                                </div>
                                <div className="">
                                  <RadialChart 
                                      percentage={Number(state.confirme_percentage)} 
                                      />  
                                </div>
                                </div> 
                                <div className="w-[100%] bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                                <div className="p-4 border-b border-gray-200">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <h3 className="text-sm font-bold text-gray-900">{t('charts.top_confirmators')}</h3>
                                      <p className="text-[10px] text-gray-600">{t('charts.top_confirmators_desc')}</p>
                                    </div>
                                  </div>
                                </div>
                                <div className="max-w-max max-h-max pb-4 pt-2">
                                  <BaarChartConfirmator data={state.top_confirmators} />
                                </div>

                                </div> 
                                <div className="w-[100%] bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                                <div className="p-4 border-b border-gray-200">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <h3 className="text-sm font-bold text-gray-900">{t('charts.delivery_time')}</h3>
                                      <p className="text-[10px] text-gray-600">{t('charts.delivery_time_desc')}</p>
                                    </div>
                                  </div>
                                </div>
                                <div className="px-0 pb-4 pt-2">
                                  <DeliveryTimeChart type={false} data={Object.entries(state.delivery_time_distribution || {}).map(([name, value]) => ({ name, value }))} />
                                </div>
                                </div> 
                              
                            </div>
                            <div className="gap-6 grid max-[1450px]:grid-cols-2 max-xl:grid-cols-1">
                              <div className="w-[100%] max-h-max p-1 bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                                <BaarChart data={state.daily_orders} />
                              </div>

                              <div className="w-[100%] max-h-max p-1 bg-white  rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                                <LinneChart data={state.daily_revenue}  />
                              </div>
                            </div>

                          </div>
                          

                          <div className=" max-[1450px]:grid max-[1450px]:grid-cols-2 max-[1450px]:col-span-2 max-sm:grid-cols-1 max-[1450px]:gap-5 space-y-6">
                            <div className="bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                              <div className="px-6 py-4 border-b border-gray-200">
                                <h3 className="text-sm font-bold text-gray-900">{t('charts.orders_by_city')}</h3>
                                <p className="text-[10px] text-gray-600">{t('charts.orders_by_city_desc')}</p>
                              </div>
                              <div className="">

                                <Map Data={state?.cities_orders}  />
                              </div>
                            </div>
                            <div className="bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                              <div className="px-6 py-4 border-b border-gray-200">
                                <h3 className="text-sm font-bold text-gray-900">{t('charts.top_products')}</h3>
                                <p className="text-[10px] text-gray-600">{t('charts.top_products_desc')}</p>
                              </div>
                              <div className="px-6 py-4">
                                {
                                  state.top_sellers?.length > 0 ? 
                                    <div className="space-y-3">
                                      {topProducts.map((product, index) => (
                                        <div 
                                          key={product.name + index} 
                                          className={`space-y-2 transition-all duration-500 ${
                                            showContent ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                                          }`}
                                          style={{
                                            transitionDelay: showContent ? `${index * 100 + 700}ms` : '0ms'
                                          }}
                                        >
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                              <div className="flex items-center justify-center w-8 h-8 rounded-lg overflow-hidden">
                                                {product.image ? (
                                                  <img 
                                                    width="32"
                                                    height="32"
                                                    alt={product.name}
                                                    src={`${BASE_URL}/storage/${product.image}`}
                                                    className="object-cover"
                                                  />
                                                ) : (
                                                  <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center">
                                                    <Package className="w-4 h-4 text-gray-400" />
                                                  </div>
                                                )}
                                              </div>
                                              <div>
                                                <p className="font-medium text-gray-900 text-xs">{product.name}</p>
                                                <p className="text-xs text-gray-500">{product.sales} {t('charts.sales')} - {product.progress.toFixed(2)}%</p>
                                              </div>
                                            </div>
                                            <div className="text-right">
                                              <p className="font-bold text-green-600 text-xs">
                                                {showContent ? product.revenue : '0.00 MAD'}
                                              </p>
                                            </div>
                                          </div>
                                          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                            <div
                                              className="bg-gradient-to-r from-[var(--color-primary-300)] to-[var(--color-primary-500)] h-2 rounded-full transition-all duration-1000 ease-out"
                                              style={{ 
                                                width: showContent ? `${product.progress}%` : '0%'
                                              }}
                                            />
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  :
                                  <div className="h-10"></div>
                                }
                              </div>
                            </div>

                            <div className="bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                              <div className="px-6 py-4 border-b border-gray-200">
                                <h3 className="text-sm font-bold text-gray-900">{t('charts.top_sellers')}</h3>
                                <p className="text-[10px] text-gray-600">{t('charts.top_sellers_desc')}</p>
                              </div>
                              <div className="px-6 py-4">
                                {
                                  state.top_sellers?.length > 0 ? 
                                    <div className="space-y-3">
                                      {topSallers?.map((saller, index) => (
                                        <div 
                                          key={index} 
                                          className={`space-y-2 transition-all duration-500 ${
                                            showContent ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                                          }`}
                                          style={{
                                            transitionDelay: showContent ? `${index * 100 + 700}ms` : '0ms'
                                          }}
                                        >
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                              <div className="flex items-center justify-center w-8 h-8 rounded-lg overflow-hidden">
                                                <div className="rounded-full p-1 bg-gradient-to-r from-[var(--color-primary-300)] to-[var(--color-primary-500)]">
                                                  <User className="w-5 h-5 text-white" />
                                                </div>
                                              </div>
                                              <div>
                                                <p className="font-medium text-gray-900 text-xs">
                                                  {saller.name}
                                                  <span className="font-100 text-gray-500 text-xs"> ({saller.name_store !== "" ? saller.name_store : ""})</span>
                                                </p>
                                                <p className="text-xs text-gray-500">{saller.total_orders} {t('charts.sales')} - {saller.progress.toFixed(2)}%</p>
                                              </div>
                                            </div>
                                            <div className="text-right">
                                              <p className="font-bold text-green-600 text-xs">
                                                {showContent ? saller.revenue : '0.00 MAD'}
                                              </p>
                                            </div>
                                          </div>
                                          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                            <div
                                              className="bg-gradient-to-r from-[var(--color-primary-300)] to-[var(--color-primary-500)] h-2 rounded-full transition-all duration-1000 ease-out"
                                              style={{ 
                                                width: showContent ? `${saller.progress}%` : '0%'
                                              }}
                                            />
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    :
                                    <div className="h-10"></div>
                                }
                              </div>
                            </div>

                            <div className="bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                              <div className="px-6 py-4 border-b border-gray-200">
                                <h3 className="text-sm font-bold text-gray-900">{t('charts.top_cities')}</h3>
                                <p className="text-[10px] text-gray-600">{t('charts.top_cities_desc')}</p>
                              </div>
                              <div className="px-6 py-4">
                                {
                                  state.top_cities?.length > 0 ?
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-xs">
                                        <thead>
                                          <tr className="border-b border-gray-100">
                                            <th className="text-left py-2 font-semibold text-gray-500">{t('charts.ville')}</th>
                                            <th className="text-right py-2 font-semibold text-gray-500">{t('charts.orders')}</th>
                                            <th className="text-right py-2 font-semibold text-gray-500">{t('charts.delivered')}</th>
                                            <th className="text-right py-2 font-semibold text-gray-500">{t('charts.rate')}</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {state.top_cities.map((city, index) => (
                                            <tr
                                              key={city.city + index}
                                              className={`border-b border-gray-50 transition-all duration-500 ${
                                                showContent ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                                              }`}
                                              style={{
                                                transitionDelay: showContent ? `${index * 100 + 700}ms` : '0ms'
                                              }}
                                            >
                                              <td className="py-2.5">
                                                <div className="flex items-center gap-2">
                                                  <MapPin className="w-3.5 h-3.5 text-[var(--color-primary-500)]" />
                                                  <span className="font-medium text-gray-900">{city.city}</span>
                                                </div>
                                              </td>
                                              <td className="text-right py-2.5 font-semibold text-gray-700">{city.total_orders}</td>
                                              <td className="text-right py-2.5 font-semibold text-gray-700">{city.delivered_orders}</td>
                                              <td className="text-right py-2.5">
                                                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                                  city.rate >= 70 ? 'bg-green-100 text-green-700' :
                                                  city.rate >= 50 ? 'bg-yellow-100 text-yellow-700' :
                                                  'bg-red-100 text-red-700'
                                                }`}>
                                                  {city.rate}%
                                                </span>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  :
                                  <div className="h-10"></div>
                                }
                              </div>
                            </div>
                          </div>
                        </div>
                        :
                        ''
                      }
                    </div>
              </div>
                  </>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
